import { useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import {
    DndContext,
    DragOverlay,
    MeasuringStrategy,
    useSensor,
    useSensors,
    type DragEndEvent,
    type DragOverEvent,
    type DragStartEvent,
    type Modifier,
} from "@dnd-kit/core";
import { BoardPointerSensor } from "@design-system/Board/boardSensors";
import type { SlateEditorHandle } from "@components/mainInterface/chat/slate/SlateEditor";
import type { DraftRefsTarget } from "../useDraftRefsByTarget";
import { isChatDragItem, type ChatDragItem } from "./chatDrag.types";
import { ChatDragContext, ChatDropTargetContext } from "./chatDragContext";
import {
    composerTargetForZone,
    smallestZoneUnderPointer,
} from "./chatDropZones";
import { chatDragLabel } from "./chatDragLabel";
import { ChatDragPreview } from "./ChatDragPreview";
import { pinPreviewToPointer } from "./pinPreviewToPointer";

// Same as ViewTable's row reordering.
const ACTIVATION_DISTANCE_PX = 8;
// Above popovers (`$zindex-popover`, 1060), like the other drag overlays.
const DRAG_OVERLAY_Z_INDEX = 1061;
// Drop zones move during a drag (the pane expands, tabs switch), so keep remeasuring.
const MEASURING = { droppable: { strategy: MeasuringStrategy.Always } };

const PREVIEW_MODIFIERS: Modifier[] = [pinPreviewToPointer];

/**
 * Lets task-body table rows and spreadsheet ranges be dragged into the task
 * chat. Owns the drag context, the preview chip, the composer highlight, and
 * what a drop does: add the refs, focus the composer, then clear a carried
 * selection.
 */
export const TaskChatDndProvider = ({ children }: { children: ReactNode }) => {
    const sensors = useSensors(
        // Rows and the cell range button rely on it skipping presses on inner controls and portals.
        useSensor(BoardPointerSensor, {
            activationConstraint: { distance: ACTIVATION_DISTANCE_PX },
        }),
    );
    const [activeItem, setActiveItem] = useState<ChatDragItem | null>(null);
    const [dropTarget, setDropTarget] = useState<DraftRefsTarget | null>(null);
    const mainComposerRef = useRef<SlateEditorHandle>(null);
    const subthreadComposerRef = useRef<SlateEditorHandle>(null);
    const contextValue = useMemo(
        () => ({
            composerRefs: {
                main: mainComposerRef,
                subthread: subthreadComposerRef,
            },
        }),
        [],
    );

    const resetDrag = () => {
        setActiveItem(null);
        setDropTarget(null);
    };

    const handleDragStart = ({ active }: DragStartEvent) => {
        const data = active.data.current;
        setActiveItem(isChatDragItem(data) ? data : null);
    };

    const handleDragOver = ({ over }: DragOverEvent) => {
        setDropTarget(over ? composerTargetForZone(over.id) : null);
    };

    const handleDragEnd = ({ active, over }: DragEndEvent) => {
        resetDrag();
        const item = active.data.current;
        const target = over ? composerTargetForZone(over.id) : null;
        if (!target || !isChatDragItem(item)) {
            return;
        }
        item.add(target);
        contextValue.composerRefs[target].current?.focus();
        item.onAdded?.();
    };

    return (
        <ChatDragContext.Provider value={contextValue}>
            <ChatDropTargetContext.Provider value={dropTarget}>
                <DndContext
                    sensors={sensors}
                    collisionDetection={smallestZoneUnderPointer}
                    measuring={MEASURING}
                    autoScroll={false}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDragEnd={handleDragEnd}
                    onDragCancel={resetDrag}
                >
                    {children}
                    {createPortal(
                        <DragOverlay
                            dropAnimation={null}
                            modifiers={PREVIEW_MODIFIERS}
                            zIndex={DRAG_OVERLAY_Z_INDEX}
                        >
                            {activeItem && (
                                <ChatDragPreview
                                    label={chatDragLabel({
                                        noun: activeItem.noun,
                                        count: activeItem.count,
                                        isOverChat: dropTarget !== null,
                                    })}
                                />
                            )}
                        </DragOverlay>,
                        document.body,
                    )}
                </DndContext>
            </ChatDropTargetContext.Provider>
        </ChatDragContext.Provider>
    );
};
